import React, { useEffect, useRef, useState } from 'react';

const TRUSTED_LOGOS = [
  { name: 'Salesforce', tag: 'CRM Connector' },
  { name: 'HubSpot', tag: 'Marketing CRM' },
  { name: 'Zapier', tag: 'Automation Layer' },
  { name: 'Jobber', tag: 'Field Service' },
  { name: 'HighLevel', tag: 'Agency CRM' },
  { name: 'Remine', tag: 'Real Estate Data' },
  { name: 'Housecall Pro', tag: 'Home Services' },
  { name: 'DealMachine', tag: 'Lead Platform' }
];

const HERO_PILLS = [
  {
    label: 'No Setup Fee',
    darkClass: 'border-cyan-400/40 bg-cyan-500/20 text-cyan-200',
    lightClass: 'border-cyan-300 bg-cyan-50 text-cyan-700'
  },
  {
    label: 'Pay Per Print',
    darkClass: 'border-blue-400/40 bg-blue-500/20 text-blue-200',
    lightClass: 'border-blue-300 bg-blue-50 text-blue-700'
  },
  {
    label: 'USA-Friendly Workflow',
    darkClass: 'border-orange-400/40 bg-orange-500/20 text-orange-200',
    lightClass: 'border-orange-300 bg-orange-50 text-orange-700'
  }
];

const USE_CASES = [
  {
    title: 'AI Editor',
    text: 'Generate editable postcard layouts in seconds with prompt-driven suggestions.',
    badge: 'Smart Layouts',
    gradient: 'from-cyan-500/20 via-blue-500/15 to-transparent'
  },
  {
    title: 'Print Workflow',
    text: 'Export and track print-ready files in one unified dashboard.',
    badge: 'Print Ready',
    gradient: 'from-orange-500/20 via-amber-500/15 to-transparent'
  },
  {
    title: 'Team Collaboration',
    text: 'Support agencies, franchises, and distributed sales teams.',
    badge: 'Multi Team',
    gradient: 'from-fuchsia-500/20 via-purple-500/15 to-transparent'
  }
];

const KPI_CARDS = [
  ['129,865+', 'Businesses served'],
  ['9x', 'Higher response over email'],
  ['1 Day', 'Average print turnaround'],
  ['24/7', 'Campaign visibility']
];

const WORKFLOW_STEPS = [
  ['1', 'Choose Template', 'Start from a branded layout or generate from an AI prompt.'],
  ['2', 'Personalize Fast', 'Drag, resize, rotate, and customize every postcard element.'],
  ['3', 'Approve & Export', 'Save JSON, download PNG, and generate print-ready PDF.'],
  ['4', 'Track Delivery', 'Monitor order status and campaign progress from one place.']
];

const PRICING_PLANS = [
  {
    name: 'Starter',
    price: '$0/mo',
    desc: 'Best for testing and first campaigns.',
    features: ['Basic editor tools', 'PNG export', 'Email support'],
    featured: false
  },
  {
    name: 'Growth',
    price: '$49/mo',
    desc: 'For active teams running weekly campaigns.',
    features: ['AI template suggestions', 'PDF export flow', 'Priority support'],
    featured: true
  },
  {
    name: 'Scale',
    price: 'Custom',
    desc: 'Advanced APIs and high-volume operations.',
    features: ['Team management', 'Order automation', 'Dedicated onboarding'],
    featured: false
  }
];

const TESTIMONIALS = [
  ['Our campaign setup time dropped by 60% in the first week.', 'Growth Lead, Home Services SaaS'],
  ['The dashboard made postcard automation simple for our operations team.', 'Ops Manager, Real Estate Group'],
  ['Design-to-print flow feels premium and client-ready from day one.', 'Agency Founder, Digital Studio']
];

const DESIGN_TEMPLATES = [
  { src: '/imgs/CAJ-1016.png', title: 'Pastel Gradient' },
  { src: '/imgs/CAJ-1026.png', title: 'Bold Split' },
  { src: '/imgs/CCC-1017.png', title: 'Serif Greeting' },
  { src: '/imgs/CCC-1018.png', title: 'Minimal Blocks' },
  { src: '/imgs/HAC-1020.png', title: 'Warm Texture' },
  { src: '/imgs/LND-1027.png', title: 'Coastal Promo' },
  { src: '/imgs/LND-1028.png', title: 'Luxury Gold' },
  { src: '/imgs/PST-1013.png', title: 'Retro Stamp' }
];

function LandingPage({ isDark }) {
  const sliderRef = useRef(null);
  const [slideIndex, setSlideIndex] = useState(0);

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return undefined;

    const interval = window.setInterval(() => {
      const cardWidth = slider.firstChild?.clientWidth || 320;
      const gap = 16;
      slider.scrollBy({ left: cardWidth + gap, behavior: 'smooth' });
      setSlideIndex((prev) => (prev + 1) % DESIGN_TEMPLATES.length);
    }, 3600);

    return () => window.clearInterval(interval);
  }, []);

  const scrollSlider = (direction) => {
    const slider = sliderRef.current;
    if (!slider) return;

    const cardWidth = slider.firstChild?.clientWidth || 320;
    const gap = 16;
    const delta = (cardWidth + gap) * (direction === 'next' ? 1 : -1);
    slider.scrollBy({ left: delta, behavior: 'smooth' });
  };

  return (
    <div className="space-y-6">
      <section
        id="solutions"
        className={`overflow-hidden rounded-[28px] border p-6 md:p-10 ${
          isDark ? 'border-slate-700 bg-slate-900/80' : 'border-white bg-white/85'
        }`}
      >
        <div className="grid items-center gap-8 lg:grid-cols-2">
          <div className="fade-in-up space-y-5">
            <span className="inline-flex rounded-full bg-cyan-500/15 px-3 py-1 text-xs font-semibold text-cyan-600">
              Marketing Teams - Agencies - Print Ops
            </span>
            <h2 className="text-4xl font-black leading-tight md:text-5xl">
              Design, Personalize, And Ship Postcards At SaaS Speed.
            </h2>
            <p className={`max-w-xl text-base md:text-lg ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              Launch colorful postcard campaigns with an AI-assisted editor, smart export flow, and print-order tracking
              in one polished dashboard.
            </p>

            <div className="flex flex-wrap items-center gap-2">
              {HERO_PILLS.map((pill) => (
                <span
                  key={pill.label}
                  className={`rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] ${
                    isDark ? pill.darkClass : pill.lightClass
                  }`}
                >
                  {pill.label}
                </span>
              ))}
            </div>
          </div>

          <div className="fade-in-up-2">
            <div className="animate-float-slow rounded-3xl border border-cyan-400/30 bg-gradient-to-br from-cyan-400/20 via-blue-500/20 to-orange-400/20 p-4 md:p-5">
              <div className={`rounded-2xl p-4 ${isDark ? 'bg-slate-950/70' : 'bg-white/80'}`}>
                <div className="mb-4 grid grid-cols-3 gap-3">
                  <div className="rounded-xl bg-blue-500/15 p-3">
                    <p className="text-xs font-semibold text-blue-600">Design Time</p>
                    <p className="text-xl font-black">-64%</p>
                  </div>
                  <div className="rounded-xl bg-cyan-500/15 p-3">
                    <p className="text-xs font-semibold text-cyan-600">Output</p>
                    <p className="text-xl font-black">2.4x</p>
                  </div>
                  <div className="rounded-xl bg-orange-500/15 p-3">
                    <p className="text-xs font-semibold text-orange-600">Speed</p>
                    <p className="text-xl font-black">Same Day</p>
                  </div>
                </div>

                <div className={`rounded-xl border p-4 ${isDark ? 'border-slate-700 bg-slate-900' : 'border-slate-200 bg-slate-50'}`}>
                  <p className="text-sm font-semibold">Campaign Snapshot</p>
                  <p className={`mt-1 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    Spring Offer - San Diego | 5,200 recipients | 6x4 glossy
                  </p>
                  <div className="mt-3 h-2 rounded-full bg-slate-200">
                    <div className="h-2 w-4/5 rounded-full bg-gradient-to-r from-cyan-400 via-blue-500 to-orange-500" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        className={`relative overflow-hidden rounded-3xl border p-5 md:p-6 ${
          isDark ? 'border-slate-700 bg-slate-900/75' : 'border-white bg-white/85'
        }`}
      >
        <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-cyan-400/15 blur-3xl" />
        <div className="absolute -left-16 -bottom-16 h-52 w-52 rounded-full bg-orange-400/15 blur-3xl" />

        <div className="relative flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.2em] text-cyan-500">Designed For You</p>
            <h3 className="mt-1 text-2xl font-black">Choose from 100s of Templates or Design Your Own</h3>
            <p className={`mt-1 text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              Curated, colorful postcard layouts tailored for USA campaigns. Swap images, edit text, export instantly.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => scrollSlider('prev')}
              className={`rounded-full border px-3 py-2 text-xs font-semibold ${
                isDark ? 'border-slate-600 bg-slate-800 text-slate-200 hover:border-cyan-400/60' : 'border-slate-200 bg-white text-slate-700 hover:border-cyan-300'
              }`}
            >
              Prev
            </button>
            <button
              type="button"
              onClick={() => scrollSlider('next')}
              className={`rounded-full border px-3 py-2 text-xs font-semibold ${
                isDark ? 'border-slate-600 bg-slate-800 text-slate-200 hover:border-cyan-400/60' : 'border-slate-200 bg-white text-slate-700 hover:border-cyan-300'
              }`}
            >
              Next
            </button>
          </div>
        </div>

        <div className="relative mt-4 overflow-hidden">
          <div
            ref={sliderRef}
            className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2"
            style={{ scrollbarWidth: 'none' }}
          >
            {DESIGN_TEMPLATES.map((item, index) => (
              <article
                key={item.src}
                className={`hover-lift snap-start rounded-2xl border p-2 shadow ${
                  isDark ? 'border-slate-700 bg-slate-900' : 'border-slate-200 bg-white'
                }`}
                style={{ minWidth: 260, maxWidth: 320 }}
              >
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                  <img
                    src={item.src}
                    alt={item.title}
                    className="h-44 w-full object-cover transition duration-500 hover:scale-[1.03]"
                  />
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <p className="text-sm font-semibold">{item.title}</p>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                    isDark ? 'bg-slate-800 text-cyan-200' : 'bg-slate-100 text-cyan-700'
                  }`}>
                    Template {index + 1}
                  </span>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-3 flex items-center gap-1">
            {DESIGN_TEMPLATES.slice(0, 6).map((_, idx) => (
              <span
                key={`dot-${idx}`}
                className={`h-2 w-2 rounded-full transition ${
                  slideIndex % DESIGN_TEMPLATES.length === idx ? 'bg-cyan-500' : 'bg-slate-400/40'
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      <section
        className={`relative overflow-hidden rounded-3xl border p-5 md:p-6 ${
          isDark ? 'border-slate-700 bg-slate-900/70' : 'border-white bg-white/85'
        }`}
      >
        <div className="absolute -right-14 -top-14 h-44 w-44 rounded-full bg-cyan-400/15 blur-3xl" />
        <div className="absolute -left-16 -bottom-14 h-48 w-48 rounded-full bg-orange-400/15 blur-3xl" />

        <div className="relative flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className={`text-xs font-black uppercase tracking-[0.2em] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              Trusted Integration Partners
            </p>
            <h3 className="mt-1 text-2xl font-black">Connected With Your Existing Stack</h3>
            <p className={`mt-1 text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              Plug into the CRM and marketing tools your team already uses.
            </p>
          </div>

          <span
            className={`rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] ${
              isDark ? 'border-cyan-500/40 bg-cyan-500/15 text-cyan-200' : 'border-cyan-300 bg-cyan-50 text-cyan-700'
            }`}
          >
            1,000+ platform friendly
          </span>
        </div>

        <div className="relative mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {TRUSTED_LOGOS.map((item, index) => (
            <article
              key={item.name}
              className={`hover-lift fade-in-up-${(index % 3) + 1} group rounded-2xl border p-3 ${
                isDark
                  ? 'border-slate-700 bg-gradient-to-br from-slate-900 to-slate-800 hover:border-cyan-400/60'
                  : 'border-slate-200 bg-gradient-to-br from-white to-slate-50 hover:border-cyan-300'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 to-blue-500 text-xs font-black text-white">
                  {item.name
                    .split(' ')
                    .map((part) => part[0])
                    .join('')
                    .slice(0, 2)}
                </span>
                <div>
                  <p className="text-sm font-bold">{item.name}</p>
                  <p className={`text-[11px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{item.tag}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section id="use-cases" className="grid gap-4 md:grid-cols-3">
        {USE_CASES.map((item, index) => (
          <article
            key={item.title}
            className={`hover-lift fade-in-up-${index + 1} card-glow relative overflow-hidden rounded-2xl border p-5 ${
              isDark ? 'border-slate-700 bg-slate-900/75' : 'border-white bg-white/80'
            }`}
          >
            <div className={`absolute inset-0 bg-gradient-to-br ${item.gradient}`} />
            <div className="relative">
              <span className="mb-3 inline-flex rounded-full bg-cyan-500/15 px-2.5 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-cyan-600">
                {item.badge}
              </span>
              <h3 className="text-base font-black tracking-wide">{item.title}</h3>
              <p className={`mt-2 text-sm leading-6 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{item.text}</p>
            </div>
          </article>
        ))}
      </section>

      <section
        className={`rounded-2xl border p-5 md:p-6 ${
          isDark
            ? 'border-slate-700 bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-800/90'
            : 'border-white bg-gradient-to-br from-white via-sky-50/60 to-orange-50/70'
        }`}
      >
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.2em] text-cyan-500">Performance Snapshot</p>
            <h3 className="mt-1 text-2xl font-black">Built For Real Marketing Results</h3>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {KPI_CARDS.map(([value, label], index) => (
            <article
              key={label}
              className={`hover-lift fade-in-up-${(index % 3) + 1} rounded-xl border p-4 ${
                isDark ? 'border-slate-700 bg-slate-900/80' : 'border-slate-200 bg-white/90'
              }`}
            >
              <p className="text-2xl font-black text-cyan-500">{value}</p>
              <p className={`mt-1 text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{label}</p>
            </article>
          ))}
        </div>
      </section>

      <section
        className={`rounded-2xl border p-5 md:p-6 ${
          isDark ? 'border-slate-700 bg-slate-900/75' : 'border-white bg-white/80'
        }`}
      >
        <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.2em] text-cyan-500">How It Works</p>
            <h3 className="mt-1 text-2xl font-black">From Idea To Mailbox In Four Steps</h3>
          </div>
          <span
            className={`rounded-full px-3 py-1 text-xs font-semibold ${
              isDark ? 'bg-slate-800 text-slate-200' : 'bg-slate-100 text-slate-700'
            }`}
          >
            Built for fast-moving SaaS teams
          </span>
        </div>

        <div className="grid gap-3 lg:grid-cols-4">
          {WORKFLOW_STEPS.map(([step, title, text], index) => (
            <article
              key={step}
              className={`hover-lift fade-in-up-${(index % 3) + 1} relative rounded-xl border p-4 ${
                isDark
                  ? 'border-slate-700 bg-gradient-to-br from-slate-900 to-slate-800 hover:border-cyan-500/50'
                  : 'border-slate-200 bg-gradient-to-br from-white to-slate-50 hover:border-cyan-300'
              }`}
            >
              {index < WORKFLOW_STEPS.length - 1 && (
                <span
                  className={`pointer-events-none absolute -right-2 top-7 hidden h-[2px] w-4 lg:block ${
                    isDark ? 'bg-cyan-500/40' : 'bg-cyan-400'
                  }`}
                />
              )}

              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 text-sm font-black text-white">
                {step}
              </span>
              <h4 className="mt-3 text-base font-bold">{title}</h4>
              <p className={`mt-2 text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{text}</p>
            </article>
          ))}
        </div>

        <div
          className={`mt-4 rounded-xl border p-4 text-sm ${
            isDark ? 'border-slate-700 bg-slate-900/80 text-slate-200' : 'border-slate-200 bg-white text-slate-700'
          }`}
        >
          Average onboarding time: under 10 minutes with guided setup for your first campaign.
        </div>
      </section>

      <section
        id="pricing"
        className={`rounded-2xl border p-5 md:p-6 ${isDark ? 'border-slate-700 bg-slate-900/75' : 'border-white bg-white/80'}`}
      >
        <div className="mb-4">
          <h3 className="text-xl font-black">Simple SaaS Pricing</h3>
          <p className={`mt-2 text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
            No setup fee. Pay for what you print and mail. Scale from single campaigns to enterprise volume.
          </p>
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          {PRICING_PLANS.map((plan, index) => (
            <article
              key={plan.name}
              className={`hover-lift fade-in-up-${(index % 3) + 1} rounded-xl border p-4 ${
                plan.featured
                  ? isDark
                    ? 'border-cyan-500/60 bg-gradient-to-br from-cyan-900/30 via-slate-900 to-blue-900/20'
                    : 'border-cyan-300 bg-gradient-to-br from-cyan-50 via-white to-blue-50'
                  : isDark
                    ? 'border-slate-700 bg-slate-900'
                    : 'border-slate-200 bg-white'
              }`}
            >
              <p className="text-xs font-black uppercase tracking-wide text-cyan-500">{plan.name}</p>
              <p className="mt-1 text-2xl font-black">{plan.price}</p>
              <p className={`mt-2 text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{plan.desc}</p>

              <div className="mt-3 space-y-1">
                {plan.features.map((feature) => (
                  <p key={feature} className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                    - {feature}
                  </p>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        {TESTIMONIALS.map(([quote, role], index) => (
          <article
            key={role}
            className={`hover-lift fade-in-up-${index + 1} relative overflow-hidden rounded-2xl border p-4 ${
              isDark ? 'border-slate-700 bg-slate-900/75' : 'border-white bg-white/85'
            }`}
          >
            <div className="absolute -right-8 -top-8 h-20 w-20 rounded-full bg-cyan-400/20 blur-2xl" />
            <p className="relative text-sm font-semibold leading-6">"{quote}"</p>
            <p className={`relative mt-3 text-xs uppercase tracking-[0.14em] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              {role}
            </p>
          </article>
        ))}
      </section>

      <section
        id="contact"
        className={`relative overflow-hidden rounded-2xl border p-5 md:p-6 ${
          isDark
            ? 'border-slate-700 bg-gradient-to-br from-slate-900 via-slate-900 to-cyan-950/40'
            : 'border-white bg-gradient-to-br from-white via-cyan-50/70 to-orange-50/70'
        }`}
      >
        <div className="absolute -right-10 -top-10 h-36 w-36 rounded-full bg-cyan-500/20 blur-3xl" />
        <div className="absolute -left-8 -bottom-8 h-28 w-28 rounded-full bg-orange-500/20 blur-3xl" />

        <div className="relative grid gap-4 md:grid-cols-2">
          <div>
            <h3 className="text-xl font-black">Need Help Launching Your First Campaign?</h3>
            <p className={`mt-2 text-sm leading-6 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              Speak with our onboarding team to set up templates, workflows, and print-ready automation for your USA
              audience.
            </p>
          </div>

          <div className="grid gap-2">
            <a
              href="tel:+18006900945"
              className={`hover-lift rounded-xl border px-4 py-3 text-sm font-semibold ${
                isDark
                  ? 'border-slate-700 bg-slate-900/70 hover:border-cyan-400/60'
                  : 'border-slate-200 bg-white/90 hover:border-cyan-300'
              }`}
            >
              Call: 1-800-690-0945
            </a>
            <a
              href="mailto:support@postcardsaas.com"
              className={`hover-lift rounded-xl border px-4 py-3 text-sm font-semibold ${
                isDark
                  ? 'border-slate-700 bg-slate-900/70 hover:border-orange-400/60'
                  : 'border-slate-200 bg-white/90 hover:border-orange-300'
              }`}
            >
              Email: support@postcardsaas.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default LandingPage;
